<template>
  <div class="stream-view" :class="{ 'disabled-stream': isDisabled }" v-press="navigateToStream">

    <div class="stream-card">
      <div class="image-container">
        <img
          @error="imageLoadFailed = true"
          :src="imageSource"
          alt=""
        />
        <img
          v-if="liveStream.logoUrl"
          class="channel-logo"
          :src="liveStream.logoUrl"
          alt=""
        />
        
        <!-- Progress bar -->
        <div v-if="hasProgress" class="progress-bar-container">
          <div class="progress-bar" :style="{ width: progressPercent + '%', backgroundColor: progressColor }" />
        </div>
      </div>

      <div class="stream-info-container" :style="textStyle">
        <p class="show-title">{{ titleText }}</p>
        <p class="show-time">{{ timeText }}</p>
        <p class="next-show" v-if="props.liveStream?.nextShow">{{ $t("liveView.next") }}: {{ nextShowText }} </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import dayjs from "dayjs";
import { CurrentLiveStream, TG4Stream, TG4StreamState } from '@/types/cms';
import { getCurrentInstance } from 'vue';
import { CMSLiveStreamManager } from '@/services/CMSLiveStreamManager';

const instance = getCurrentInstance();
const router = instance?.proxy?.$router;

const props = defineProps<{
  liveStream: CurrentLiveStream;
  textColor?: string;
}>();

const imageLoadFailed = ref(false);
const currentShow = computed(() => props.liveStream.currentShow);
const isStartTimeInFuture = computed(() => currentShow.value?.startTime ? dayjs(currentShow.value.startTime).isAfter(dayjs()) : false);

const isDisabled = computed(() => {
  if (!props.liveStream) return false;
  return props.liveStream.state === TG4StreamState.DISABLED || props.liveStream.state === TG4StreamState.UPCOMING;
});

const isCula4 = computed(() => {
  return props.liveStream?.stream === TG4Stream.CULA4;
});

const hasProgress = computed(() => {
  const show = props.liveStream?.currentShow;
  return show && show.startTime && show.endTime && !isStartTimeInFuture.value;
});

const progressColor = computed(() => {
  return isCula4.value ? '#a0f5b8' : '#e91e90';
});

const imageSource = computed(() => {
  if (imageLoadFailed.value) {
    return `assets/images/tg4FallbackImageSmall.png`;
  }

  // Use Cloudinary image from pcode
  if (props.liveStream?.currentShow?.pcode) {
    return `https://res.cloudinary.com/tg4/image/upload/w_700,h_395,c_fit,q_auto:eco/${props.liveStream.currentShow.scode}.jpg`;
  }

  return `assets/images/placeholder.png`;
});

const progressPercent = computed(() => {
  if (!props.liveStream?.currentShow?.startTime || !props.liveStream?.currentShow?.endTime) {
    return 0;
  }

  const start = dayjs(props.liveStream.currentShow.startTime);
  const end = dayjs(props.liveStream.currentShow.endTime);
  const now = dayjs();

  const total = end.diff(start, 'second');
  const elapsed = now.diff(start, 'second');

  if (total <= 0) return 0;
  return Math.min(100, Math.max(0, (elapsed / total) * 100));
});

const titleText = computed(() => {
  return currentShow.value?.title ?? '';
});

const timeText = computed(() => {
  const show = currentShow.value;
  if (!show?.startTime && !show?.endTime) return '';

  if (isStartTimeInFuture.value){
    return formatTimeAndDate(show.startTime);
  };


  var startTime = formatTime(show.startTime);
  if (!startTime) return '';
  var result = startTime;
  var endTime = formatTime(show.endTime);
  if (endTime) {
    result += ` - ${endTime}`;
  }
  return result;
});

const nextShowText = computed(() => {
  const nextShow = props.liveStream?.nextShow;
  if (!nextShow) return '';
  return nextShow.title ?? '';
});

const formatTime = (timeStr: string | null): string | null => {
  if (!timeStr) return null;
  return dayjs(timeStr).format("HH:mm");
};


const navigateToStream = () => {
  const stream = props.liveStream;
  if (!stream || !CMSLiveStreamManager.isStreamPlayable(stream)) return;
  router?.push({ path: CMSLiveStreamManager.playerPath(stream) });
};

const formatTimeAndDate = (timeStr: string | null): string => {
  if (!timeStr) return '';
  const date = new Date(timeStr);
  return date.toLocaleString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
    hour12: false
  });
};

const textStyle = computed(() => {
  if (props.textColor) {
    return {
      color: props.textColor,
    };
  }
  return {};
});

</script>

<style scoped lang="scss">
@use "@/assets/styles/loading-animation.scss";

.stream-view {
  width: $episode-cell-width;
  min-width: $episode-cell-width;
  outline: 0;
  cursor: pointer;
  transition: transform 0.3s ease-in-out;

  &:focus {

    .stream-card .image-container {
      border-color: $color-pink;
    }
  }

  &.disabled-stream {
    opacity: 0.5;
    pointer-events: none;
  }

  .stream-card {
    display: flex;
    flex-direction: column;

    .image-container {
      position: relative;
      width: 100%;
      height: 0;
      padding-bottom: 56.25%;
      background-color: $color-dark-grey;
      background-clip: padding-box;
      box-sizing: border-box;
      border-width: $focus-border-width;
      border-color: transparent;
      border-style: solid;

      img {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .channel-logo {
        position: absolute;
        top: 8px;
        right: 8px;
        left: auto;
        width: auto;
        height: 30px;
        object-fit: contain;
      }
    }

    .progress-bar-container {
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 6px;
      background-color: $color-dark-grey;

      .progress-bar {
        height: 100%;
        background-color: $color-pink;
        transition: width 1s linear;
      }
    }

    .stream-info-container {
      min-height: 60px;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: flex-start;

      .show-title {
        @include text-cell-title;
        @include text-white;
        @include line-clamp(1);
        overflow: hidden;
        text-align: left;
      }
      .show-time {
        @include text-cell-subtitle;
        @include text-white;
        @include line-clamp(1);
      }
      .next-show {
        @include text-cell-subtitle;
        @include text-white;
        @include line-clamp(1);
        margin-top: 4px;
      }
    }
  }
}

</style>
